/**
 * CountdownTimer Component
 * Displays a live countdown to the free period end date
 */

import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

const FREE_PERIOD_END = process.env.REACT_APP_FREE_PERIOD_END || '2026-06-01T00:00:00Z';

function CountdownTimer({ compact = false, showIcon = true, className = '' }) {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  function calculateTimeLeft() {
    const endDate = new Date(FREE_PERIOD_END).getTime();
    const now = Date.now();
    const difference = endDate - now;

    if (difference <= 0) {
      return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      expired: false,
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000)
    };
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  if (timeLeft.expired) {
    return (
      <div className={`text-center ${className}`}>
        <p className="text-navy-600 font-medium">Free registration period has ended</p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {showIcon && <Clock className="h-4 w-4 text-primary-600" />}
        <span className="text-sm font-medium text-navy-700">
          {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m left
        </span>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-6 ${className}`}>
      <div className="text-center">
        {showIcon && (
          <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-full mb-4">
            <Clock className="h-6 w-6 text-primary-600" />
          </div>
        )}
        <h3 className="text-lg font-semibold text-navy-900 mb-2">
          Yacht Owners: Free Until June 1st, 2026
        </h3>
        <p className="text-sm text-navy-600 mb-4">
          Register now and use 1crew3 free until the countdown ends. After June 1st, a subscription is required.
        </p>

        <div className="flex justify-center gap-3">
          <TimeUnit value={timeLeft.days} label="Days" />
          <TimeUnit value={timeLeft.hours} label="Hours" />
          <TimeUnit value={timeLeft.minutes} label="Min" />
          <TimeUnit value={timeLeft.seconds} label="Sec" />
        </div>
      </div>
    </div>
  );
}

function TimeUnit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="bg-white rounded-lg shadow-sm border border-navy-100 px-3 py-2 min-w-[60px]">
        <span className="text-2xl font-bold text-navy-900">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="text-xs text-navy-500 mt-1 font-medium">{label}</span>
    </div>
  );
}

// Helper to check if we're still in free period
export function isFreePeriod() {
  return Date.now() < new Date(FREE_PERIOD_END).getTime();
}

export function getFreePeriodEnd() {
  return FREE_PERIOD_END;
}

export default CountdownTimer;
